import { useState } from "react";

import { ChartPanel, DataTable, type DataTableColumn } from "./dashboardKit";
import { CARE_NAV_TABLE_MAX_HEIGHT_CLASS, asCount } from "./careNavigationShared";
import type {
  CalendarStatusFilter,
  CareNavigationCalendarRow,
  CareNavigationCalendarStatusRequest,
} from "./careNavigationTypes";
import { exportRowsToCsv } from "./dashboardsExport";
import { fetchCareNavigationCalendarStatus } from "./dashboardsReactQuery";

const STATUS_OPTIONS: Array<{ value: CalendarStatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "needs_scheduling", label: "Needs scheduling" },
  { value: "scheduled_in_window", label: "Scheduled in window" },
  { value: "scheduled_future", label: "Scheduled future" },
  { value: "stale_no_encounter", label: "Stale / no encounter" },
  { value: "annual_assessment_scheduled", label: "Annual assessment scheduled" },
  { value: "cancelled_only", label: "Cancelled only" },
  { value: "completed_this_month", label: "Completed this month" },
];

const CALENDAR_STATUS_DEFINITION =
  "Diagnostic calendar status for the current aligned Carespace cohort; the Needs scheduling filter follows the Carespaces monthly billing and booking rule and can include other calendar statuses. Annual assessment scheduled means an active annual-assessment booking in the current or a future month; it is a scheduling signal, not the Reassessment Completion KPI. Stale means a past active care appointment with no completed billable encounter, completed initial or annual assessment, or no-show on the same local date. Any encounter type can satisfy the appointment; assessment completion counts before billing posts.";

export function getCalendarWeekStarts(dateStart: string, dateEnd: string): string[] {
  const start = new Date(`${dateStart}T00:00:00Z`);
  const end = new Date(`${dateEnd}T00:00:00Z`);
  if (Number.isNaN(start.valueOf()) || Number.isNaN(end.valueOf()) || start > end) return [];
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  const weeks: string[] = [];
  for (let current = start.getTime(); current <= end.getTime(); current += 7 * 86_400_000) {
    weeks.push(new Date(current).toISOString().slice(0, 10));
  }
  return weeks;
}

export function buildCalendarCsvRows(rows: CareNavigationCalendarRow[], weeks: string[]) {
  return rows.map(({ weekly_attempts, ...row }) => ({
    ...row,
    ...Object.fromEntries(
      weeks.flatMap((week) => [
        [`${week} calls`, weekly_attempts?.[week]?.calls ?? 0],
        [`${week} texts`, weekly_attempts?.[week]?.texts ?? 0],
        [`${week} emails`, weekly_attempts?.[week]?.emails ?? 0],
      ]),
    ),
  }));
}

export async function loadCompleteCalendarRows(
  request: CareNavigationCalendarStatusRequest,
  fetchPage: typeof fetchCareNavigationCalendarStatus = fetchCareNavigationCalendarStatus,
): Promise<CareNavigationCalendarRow[]> {
  const rows: CareNavigationCalendarRow[] = [];
  let expected = 0;
  do {
    const page = await fetchPage({ ...request, limit: 1000, offset: rows.length });
    expected =
      request.status === "all" || !request.status
        ? (page.summary.total ?? 0)
        : (page.summary[request.status] ?? 0);
    if (page.rows.length === 0 && rows.length < expected) {
      throw new Error("Calendar export returned an incomplete page");
    }
    rows.push(...page.rows);
  } while (rows.length < expected);
  return rows;
}

export function buildCareNavigationCalendarColumns(
  weeks: string[] = [],
): Array<DataTableColumn<CareNavigationCalendarRow>> {
  return [
    {
      key: "carespace_id",
      label: "Carespace",
      definition: "Aligned carespace represented by this follow-up row.",
      sortable: true,
      render: (row) =>
        row.carespace_url ? (
          <a
            href={row.carespace_url}
            target="_blank"
            rel="noreferrer"
            className="text-foreground underline-offset-2 hover:underline"
          >
            {row.carespace_id}
          </a>
        ) : (
          row.carespace_id
        ),
    },
    {
      key: "navigator",
      label: "Navigator",
      definition: "Care Navigator currently assigned to the carespace.",
      sortable: true,
    },
    {
      key: "clinic",
      label: "Clinic",
      definition: "Clinic associated with the carespace.",
      sortable: true,
    },
    {
      key: "calendar_status",
      label: "Status",
      definition: CALENDAR_STATUS_DEFINITION,
      sortable: true,
    },
    {
      key: "needs_scheduling_this_month",
      label: "Needs scheduling this month",
      definition:
        "Matches the Carespaces aligned Needs scheduling this month filter, even when a diagnostic calendar status says scheduled future or stale.",
      render: (row) => (row.needs_scheduling_this_month ? "Yes" : "No"),
      sortable: true,
    },
    {
      key: "outreach_attempts_total",
      label: "Manual outreach MTD",
      align: "right",
      definition:
        "All completed call attempts (answered and unanswered), texts, and manually logged emails on this currently assigned carespace during the selected month, regardless of who logged them.",
      render: (row) => asCount(row.outreach_attempts_total),
      sortable: true,
    },
    {
      key: "call_attempts",
      label: "Calls MTD",
      align: "right",
      definition: "Completed call attempts, answered or unanswered, excluding text messages.",
      render: (row) => asCount(row.call_attempts),
      sortable: true,
    },
    {
      key: "voicemail_attempts",
      label: "VM MTD",
      align: "right",
      definition:
        "Completed call attempts logged with a voicemail outcome for this carespace during the selected month.",
      render: (row) => asCount(row.voicemail_attempts),
      sortable: true,
    },
    {
      key: "text_attempts",
      label: "Texts MTD",
      align: "right",
      definition:
        "Completed call activities logged with text-message contact for this carespace during the selected month.",
      render: (row) => asCount(row.text_attempts),
      sortable: true,
    },
    {
      key: "email_attempts",
      label: "Emails MTD",
      align: "right",
      definition:
        "Completed, manually logged email activities for this carespace during the selected month. Email billing remains a separate metric.",
      render: (row) => asCount(row.email_attempts),
      sortable: true,
    },
    ...weeks.flatMap((week) =>
      (["calls", "texts", "emails"] as const).map((kind) => ({
        key: `${week}-${kind}`,
        label: `${kind} ${week}`,
        align: "right" as const,
        definition: `${kind} logged during the week beginning ${week} (Monday, dashboard timezone).`,
        render: (row: CareNavigationCalendarRow) =>
          asCount(row.weekly_attempts?.[week]?.[kind] ?? 0),
        sortValue: (row: CareNavigationCalendarRow) => row.weekly_attempts?.[week]?.[kind] ?? 0,
        sortable: true,
      })),
    ),
    {
      key: "event_title",
      label: "Event",
      definition: "Calendar event title for the appointment represented by this row.",
      sortable: true,
    },
    {
      key: "booking_start_at",
      label: "Booking start",
      definition: "Scheduled appointment start in your local browser time.",
      sortable: true,
      sortValue: (row) => row.booking_start_at || null,
      render: (row) =>
        row.booking_start_at ? new Date(row.booking_start_at).toLocaleString() : "—",
    },
  ];
}

export function CareNavigationCalendar(props: {
  error?: string;
  exportRequest?: CareNavigationCalendarStatusRequest;
  loading?: boolean;
  onStatusChange: (status: CalendarStatusFilter) => void;
  rows: CareNavigationCalendarRow[];
  status: CalendarStatusFilter;
  summary: Record<string, number>;
}) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const weeks = props.exportRequest
    ? getCalendarWeekStarts(props.exportRequest.dateStart, props.exportRequest.dateEnd)
    : [];
  const columns = buildCareNavigationCalendarColumns(weeks);
  const matchingCount =
    props.status === "all" ? (props.summary.total ?? 0) : (props.summary[props.status] ?? 0);

  const exportCompleteCsv = async () => {
    if (!props.exportRequest) return;
    setExporting(true);
    setExportError("");
    try {
      const rows = await loadCompleteCalendarRows(props.exportRequest);
      await exportRowsToCsv(buildCalendarCsvRows(rows, weeks), "care-navigation-calendar.csv");
    } catch {
      setExportError("Could not export the complete cohort. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <ChartPanel
      title="Calendar follow-up"
      action={
        <button
          type="button"
          className="rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => void exportCompleteCsv()}
          disabled={exporting || props.loading || !props.exportRequest || !matchingCount}
        >
          {exporting ? "Exporting…" : "Download full CSV"}
        </button>
      }
    >
      <p className="mb-3 text-xs text-muted-foreground">
        Weekly and month-to-date counts include all completed call attempts (answered or
        unanswered), texts, and manually logged emails on each currently assigned carespace. Calls
        exclude texts.
      </p>
      <p className="mb-3 text-xs text-muted-foreground">
        Annual assessment scheduled counts active annual-assessment bookings for the current aligned
        cohort when the booking is in the current or a future month. It is a scheduling signal, not
        the Reassessment Completion KPI, and it does not increase monthly encounter projections.
        Reassessment Completion measures qualifying completed reassessments for the cohort due in
        the selected month, from the reassessment window opening through the selected period end.
      </p>
      <div className="mb-3 flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((option) => {
          const count =
            option.value === "all"
              ? Number(props.summary.total || 0)
              : Number(props.summary[option.value] || 0);
          const active = props.status === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => props.onStatusChange(option.value)}
              className={
                active
                  ? "rounded-md bg-foreground px-2 py-1 text-xs text-background"
                  : "rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground"
              }
            >
              {option.label} ({asCount(count)})
            </button>
          );
        })}
      </div>

      {exportError ? (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {exportError}
        </p>
      ) : null}
      {props.rows.length < matchingCount ? (
        <p className="mb-2 text-xs text-muted-foreground">
          Showing the first {props.rows.length} rows. Download full CSV for every matching
          carespace.
        </p>
      ) : null}
      {props.loading ? (
        <p className="text-sm text-muted-foreground">Loading calendar status…</p>
      ) : props.error ? (
        <p className="text-sm text-red-600">{props.error}</p>
      ) : (
        <DataTable
          columns={columns}
          maxHeightClass={CARE_NAV_TABLE_MAX_HEIGHT_CLASS}
          rows={props.rows}
          rowKey={(row, index) => `${row.carespace_id}-${index}`}
          emptyMessage="No calendar rows for this filter."
        />
      )}
    </ChartPanel>
  );
}
